import dayjs from 'dayjs';
import lodashFilter from 'lodash/filter';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';

import $axios from '~/services/AxiosService';
import QLogger from '~/services/QLogger';

import i18n from '~/services/i18n';
import Constants from '~/config/Constants';
import ToastService from '~/services/ToastService';
import CookieService from '~/services/CookieService';
import UserService from '~/services/UserService';
import $store from '~/services/StoreService';
import BaseURLS from '~/config/BaseURLs';
import { getEnvironmentEnv } from '~/utils/EnvUtils';
import UrlUtils from '~/utils/UrlUtils';

export default class GameAPIService {
  static async reopenGame(gameId, expiry) {
    try {
      const data = {
        expiry,
      };
      const response = await $axios().post(`/game/${gameId}/reopen`, data);
      const result = response.data;
      if (result.success) {
        return result.data;
      }
      return null;
    } catch (error) {
      QLogger.log('Error at GameAPIService.reopenGame', error);
      ToastService.add({
        type: Constants.ToastTypes.ERROR,
        icon: 'fas fa-times-circle',
        title: i18n('Something went wrong while reopening the game!'),
      });
      return {
        success: false,
        error,
      };
    }
  }

  static async renameGame(gameId, newName, gameState, dataLocation) {
    try {
      const data = {
        name: newName,
        state: gameState,
        dataLocation,
      };
      const response = await $axios().put(`/game/${gameId}/rename`, data);
      const result = response.data;
      if (result.success) {
        return result.data;
      }
      return null;
    } catch (err) {
      QLogger.log('Error at GameAPIService.renameGame', err);
      ToastService.add({
        type: Constants.ToastTypes.ERROR,
        icon: 'fas fa-times-circle',
        title: i18n('Something went wrong while renaming the game!'),
      });
    }
  }

  static async renameCourseName(sessionId, hostId, newName) {
    try {
      const data = {
        name: newName,
      };
      const response = await $axios().patch(`/learning-track-session/rename/${sessionId}/${hostId}`, data);
      const result = response.data;
      if (result.success) {
        return result.data;
      }
      return null;
    } catch (err) {
      QLogger.log('Error at GameAPIService.renameCourseName', err);
      ToastService.add({
        type: Constants.ToastTypes.ERROR,
        icon: 'fas fa-times-circle',
        title: i18n('Something went wrong while renaming the course session!'),
      });
      return null;
    }
  }

  static async deleteGame(gameId, gameState) {
    try {
      const gameData = [{
        id: gameId,
        state: gameState,
      }];
      const response = await $axios().delete('/games', { data: { games: gameData } });
      const result = response.data;
      if (result?.success) {
        return result.data;
      }
      return null;
    } catch (err) {
      QLogger.log('Error at GameAPIService.deleteGame', err);
      ToastService.add({
        type: Constants.ToastTypes.ERROR,
        icon: 'fas fa-times-circle',
        title: i18n('Something went wrong while deleting the game!'),
      });
    }
  }

  static async deleteGames(payload) {
    try {
      const response = await $axios().delete('/games', { data: { games: payload } });
      const result = response.data;
      if (result.success) {
        return result.data;
      }
      return null;
    } catch (err) {
      QLogger.log('Error at GameAPIService.deleteGame', err);
      ToastService.add({
        type: Constants.ToastTypes.ERROR,
        icon: 'fas fa-times-circle',
        title: i18n('Something went wrong while deleting the game!'),
      });
    }
  }

  static async search({
    pageNumber,
    startDate,
    endDate,
    queryTerm = '',
    classIds = [],
    filter = '',
    sortKey = undefined,
    size = 10,
    quizIds = [],
    gameState = [],
    gameType = [],
    paidOrganizationId,
    hostedBy = [],
    mock = false,
  }) {
    try {
      const filterList = {
        gameState: gameState && gameState.length ? gameState : ['completed', 'running', 'waiting', 'stopped', 'scheduled', 'paused'],
        quizId: quizIds,
      };

      if (paidOrganizationId) {
        filterList.paidOrganization = [paidOrganizationId];
      }

      const user = new UserService($store()?.state.root?.user);

      if (filter === 'mine') {
        filterList.createdBy = user.id;
      } else if (filter === 'shared') {
        if (user.isCorporate) {
          filterList.sharedWith = [`${user.id}:${user.paidOrganization}`];
        } else {
          filterList.sharedWith = [user.id];
        }
      }

      if (gameType.length > 0) {
        filterList.gameType = gameType;
      }

      const response = await $axios().get('/games', {
        params: {
          query: queryTerm,
          from: pageNumber * 10,
          size,
          filterList,
          sortKey,
          groupIds: JSON.stringify(classIds),
          rangeFilter: {
            createdAt: {
              from: startDate,
              to: endDate,
            },
          },
          hostedBy,
          paidOrganizationId,
          mock,
        },
      });
      const result = response.data;

      if (result.success) {
        result.data.games = lodashFilter(result.data.games, item => !!item._id);

        result.data.games.forEach((game, index) => {
          if ((game.gameType === 'async' || game.gameType === 'pres_async') && game.gameState === 'running') {
            // Checking if (createdAt + expiry) is before current time
            // This is needed to show the correct value
            if (dayjs(game.createdAt).add(game.expiry, 'seconds').isBefore(new Date())) {
              result.data.games[index].gameState = 'completed';
            }
          }
        });

        return result.data;
      }
      return null;
    } catch (err) {
      QLogger.log('Error at GameAPIService.search', err);
    }
  }

  static async getGame(gameId, queryParams = {}) {
    try {
      if (!gameId || gameId === 'undefined' || gameId === 'null') {
        QLogger.error(i18n('gameId is invalid'));
        throw new Error(i18n('gameId is invalid'));
      }
      const { data } = await $axios().get(`/game/${gameId}`, { params: queryParams });
      return data;
    } catch (err) {
      QLogger.error(err);
    }
  }

  static async startGame(roomHash, expiry) {
    try {
      if (!roomHash) {
        QLogger.error('roomHash is invalid');
      }
      const payload = {
        roomHash,
      };
      if (expiry) {
        payload.expiry = expiry;
      }
      const { data } = await $axios().post('/game/start', payload);
      return data;
    } catch (err) {
      QLogger.error(err);
    }
  }

  static async startScheduledGame(roomHash, expiry) {
    try {
      if (!roomHash) {
        QLogger.error('roomHash is invalid');
        throw new Error('GameId (RoomHash) is invalid');
      }

      const payload = { roomHash };
      if (expiry) { payload.expiry = expiry; }

      const { data } = await $axios().post('/game/scheduled-to-waiting-or-running', payload);

      return data;
    } catch (err) {
      QLogger.error(err);
      throw new Error(err);
    }
  }

  static async assignGCLByGroups(gameId, assignedClasses) {
    try {
      if (!gameId) {
        QLogger.error('gameId is invalid');
      }
      const assignedTo = isEmpty(assignedClasses.assignedTo) ? null : assignedClasses.assignedTo;
      const groupIds = map(assignedClasses.classes, item => item._id);
      const translatedDescription = i18n('Click on the link below to start the game');
      const data = {
        title: assignedClasses.title,
        description: assignedClasses.desc || translatedDescription,
        startDate: assignedClasses.dateToStart,
        groupIds,
        assignedTo,
        grading: assignedClasses.grading,
      };
      const res = await $axios().post(`/game/${gameId}/assign-gcl-bygroups`, data);
      return res;
    } catch (err) {
      QLogger.error(err);
      return err?.response || {};
    }
  }

  static async updateGroupAccommodations(gameId, groupIds) {
    try {
      if (!gameId) {
        QLogger.error('gameId is invalid');
      }
      const res = await $axios().post(`/game/${gameId}/update-group-accommodations`, { groupIds });
      return res;
    } catch (err) {
      QLogger.error(err);
      return err?.response || {};
    }
  }

  static async emailReportToParent(gameId, recipients) {
    const CSRFToken = CookieService.get('x-csrf-token');
    const headers = {
      'x-csrf-token': CSRFToken,
    };
    const payload = {
      gameId,
      recipients,
    };
    try {
      const response = await $axios().post('/emails/send', payload, { headers });
      if (!response?.data?.success) {
        throw new Error(i18n('Something went wrong processing this request!'));
      }
      ToastService.add({
        type: Constants.ToastTypes.SUCCESS,
        icon: 'fas fa-times-circle',
        title: i18n('Email sent successfully!'),
      });
    } catch (err) {
      ToastService.add({
        type: Constants.ToastTypes.ERROR,
        icon: 'fas fa-times-circle',
        title: i18n('Email cannot be sent. Please try again!'),
      });
      QLogger.error(err);
      throw err;
    }
  }

  static async downloadGame(gameId) {
    const regex = /GMT\+([0-9]+)/;

    const d = new Date();
    const result = regex.exec(d) || [];
    const timezone = `GMT+${result[1] || '000'}`;

    const CSRFToken = CookieService.get('x-csrf-token');
    const headers = {
      'x-csrf-token': CSRFToken,
    };
    const params = {
      offset: timezone,
      _: Math.floor(Math.random() * 90000) + 10000,
    };

    try {
      const response = await $axios().get(`/savexls/${gameId}`, { headers, params });
      return response;
    } catch (err) {
      QLogger.error(err);
      throw err;
    }
  }

  static async submitGameFeedback(params, isDismissed) {
    try {
      const headers = {
        type: 'POST',
        dataType: 'json',
        contentType: 'application/json',
      };
      const payload = {
        quizizz_uid: CookieService.get('quizizz_uid'),
        slot: CookieService.get('QUIZIZZ_EXP_SLOT') || '0',
        experiment: CookieService.get('QUIZIZZ_EXP_NAME') || 'main',
        isDismissed,
        eventName: 'ft_error_review',
        platform: 'web',
        country: $store()?.getters?.['root/serverData']?.requestCountry || 'IN',
        timezoneOffset: (new Date()).getTimezoneOffset(),
        params,
      };
      return await $axios().post('https://analytics.quizizz.com/feedback2', payload, headers);
    } catch (error) {
      ToastService.add({
        type: Constants.ToastTypes.ERROR,
        icon: 'fas fa-times-circle',
        title: i18n('Feedback cannot be submitted. Please try again!'),
      });
      QLogger.error(error);
      throw (error);
    }
  }

  static async updateFocusModeTimer(gameId, totalTimeLeft, delta) {
    try {
      if (!gameId) {
        QLogger.error('roomHash is invalid');
      }
      const CSRFToken = CookieService.get('x-csrf-token');
      const headers = {
        'x-csrf-token': CSRFToken,
      };
      const response = await $axios().put(`/game/${gameId}/updateFocusModeTimer`, {
        totalTimeLeft,
        delta,
      }, { headers });
      if (!response?.data?.success) {
        throw new Error('Something went wrong while updating time. Please try again');
      }
    } catch (err) {
      QLogger.error(err);
      throw err;
    }
  }

  static async updateMasteryPeakTimer(gameId, isMasteryPeakTimerEnabled = false) {
    try {
      if (!gameId) {
        QLogger.error('roomHash is invalid');
      }
      const CSRFToken = CookieService.get('x-csrf-token');
      const headers = {
        'x-csrf-token': CSRFToken,
      };
      const response = await $axios().put(`/game/${gameId}/updateMasteryPeakTimer`, {
        isMasteryPeakTimerEnabled,
      }, { headers });
      if (!response?.data?.success) {
        throw new Error('Something went wrong while updating time. Please try again');
      }
    } catch (err) {
      QLogger.error(err);
      throw err;
    }
  }

  static async pingTimeLeft(gameId, timeLeft = Constants.MasteryPeakTimer) {
    try {
      if (!gameId) {
        QLogger.error('roomHash is invalid');
      }
      const CSRFToken = CookieService.get('x-csrf-token');
      const headers = {
        'x-csrf-token': CSRFToken,
      };
      const response = await $axios().post(`/game/${gameId}/pingTimeLeft`, {
        timeLeft,
      }, { headers });
      if (!response?.data?.success) {
        throw new Error('Something went wrong while pinging time. Please try again');
      }
    } catch (err) {
      QLogger.error(err);
      throw err;
    }
  }

  static async replayGame(gameId) {
    try {
      if (!gameId) {
        QLogger.error('roomHash is invalid!');
      }
      const CSRFToken = CookieService.get('x-csrf-token');
      const headers = {
        'x-csrf-token': CSRFToken,
      };
      const response = await $axios().post(`/game/${gameId}/replay`, {}, headers);
      return response;
    } catch (err) {
      QLogger.error(err);
      throw err;
    }
  }

  static async sendMessageToGCL(gameId, lmsName = 'gcl', forceRedirect = true) {
    try {
      const response = await $axios().post(`/game/${gameId}/assign-gcl-groups`);
      const result = response.data;
      if (result.success) {
        ToastService.add({
          type: Constants.ToastTypes.SUCCESS,
          icon: 'fas fa-times-circle',
          title: i18n('Classes assigned successfully!'),
        });
        return { success: true, data: result.data };
      }

      ToastService.add({
        type: Constants.ToastTypes.ERROR,
        icon: 'fas fa-times-circle',
        title: i18n('Something went wrong while assigning lms classes'),
      });
      return { success: false, data: result.data };
    } catch (err) {
      QLogger.log('Error at GameAPIService.sendMessageToGCL', err);
      const errResponse = err.response;
      if (forceRedirect && errResponse?.data?.error && errResponse.data.error === 'user.NO_AUTH') {
        const url = `/lms/${lmsName}?switch=true&hasAutoSyncPermission=true&state=${encodeURIComponent(JSON.stringify({
          redirect: UrlUtils.toString({
            urlData: { path: window.location.href },
            query: { lmsConnectSuccess: true, lmsConnectType: lmsName, givenGclSyncPermission: true },
          }),
        }))}`;
        window.location.href = url;
      }
      ToastService.add({
        type: Constants.ToastTypes.ERROR,
        icon: 'fas fa-times-circle',
        title: i18n('Something went wrong while assigning lms classes'),
      });
      return { success: false, error: errResponse?.data };
    }
  }

  static async updateGameName(gameId, newName, gameState) {
    try {
      const response = await $axios().put(`/game/${gameId}/rename`, {
        name: newName,
        state: gameState,
      });

      if (response.data.success) {
        return {
          success: true,
          name: newName,
        };
      }

      return {
        success: false,
        response,
      };
    } catch (error) {
      QLogger.log('Error at GameAPIService.updateGameName');
      ToastService.add({
        type: Constants.ToastTypes.ERROR,
        icon: 'fas fa-times-circle',
        title: i18n('Something went wrong while renaming the report'),
      });
      return {
        success: false,
        error,
      };
    }
  }

  static async deletePlayer(gameId, payload) {
    try {
      const CSRFToken = CookieService.get('x-csrf-token');
      const headers = {
        'x-csrf-token': CSRFToken,
      };

      const response = await $axios().delete(`/game/${gameId}/player`, { data: payload }, { headers });

      if (response?.data?.success) {
        ToastService.add({
          type: Constants.ToastTypes.SUCCESS,
          icon: 'fas fa-times-circle',
          title: i18n('Player deleted successfully!'),
        });
        return { success: true };
      }

      QLogger.log('Error at GameAPIService.deletePlayer');
      ToastService.add({
        type: Constants.ToastTypes.ERROR,
        icon: 'fas fa-times-circle',
        title: i18n('Something went wrong while deleting the player'),
      });
      return { success: false };
    } catch (error) {
      QLogger.log('Error at GameAPIService.deletePlayer', error);
      ToastService.add({
        type: Constants.ToastTypes.ERROR,
        icon: 'fas fa-times-circle',
        title: i18n('Something went wrong while deleting the player'),
      });
      return {
        success: false,
        error,
      };
    }
  }

  static async rescheduleGame(payload) {
    try {
      const response = await $axios().post('/game/schedule', payload);
      ToastService.add({
        type: Constants.ToastTypes.SUCCESS,
        icon: 'fas fa-check-circle',
        title: i18n('Scheduled Time udpated!'),
      });
      return response;
    } catch (error) {
      QLogger.log('Error at GameAPIService.rescheduleGame', error);
      ToastService.add({
        type: Constants.ToastTypes.ERROR,
        icon: 'fas fa-times-circle',
        title: i18n('Something went wrong'),
      });
    }
  }

  static async updateDeadline({ gameId, expiry }) {
    const response = await $axios().put(`/game/${gameId}/expiry`, {
      expiry,
    });

    return response.data;
  }

  static async finishGame(gameId) {
    try {
      const response = await $axios().post(`/game/${gameId}/getReport`);
      return response;
    } catch (error) {
      QLogger.log('Error at GameAPIService.finishGame', error);
      ToastService.add({
        type: Constants.ToastTypes.ERROR,
        icon: 'fas fa-times-circle',
        title: i18n('Something went wrong'),
      });
      return null;
    }
  }

  static async updateResponseMarks(gameId, responseMarksMap, responseComposites) {
    try {
      const CSRFToken = CookieService.get('x-csrf-token');
      const headers = { 'x-csrf-token': CSRFToken };
      const response = await $axios().post(`/game/${gameId}/rescore`, {
        type: 'marks',
        responseUpdates: responseMarksMap,
        responseComposites,
      }, { headers });
      if (response?.data?.success) {
        ToastService.add({
          type: Constants.ToastTypes.SUCCESS,
          icon: 'fas fa-check-circle',
          title: i18n('Points updated successfully!'),
        });
        return response.data.data;
      }

      QLogger.log('Error at GameAPIService.updateResponseMarks', response.data);
      ToastService.add({
        type: Constants.ToastTypes.ERROR,
        icon: 'fas fa-times-circle',
        title: i18n('Something went wrong while updating scores'),
      });
      return null;
    } catch (error) {
      QLogger.log('Error at GameAPIService.updateResponseMarks', error);
      ToastService.add({
        type: Constants.ToastTypes.ERROR,
        icon: 'fas fa-times-circle',
        title: i18n('Something went wrong while updating scores'),
      });
      return null;
    }
  }

  static async editGameSettings(gameId, gameOptions) {
    try {
      const response = await $axios().post(`/game/${gameId}/updateGameOptions`, { gameOptions });
      return response.data.data;
    } catch (err) {
      QLogger.log('Error at GameAPIService.editGameSettings', err);
      ToastService.add({
        type: Constants.ToastTypes.ERROR,
        icon: 'fas fa-times-circle',
        title: i18n('Something went wrong while updating the settings'),
      });
      throw err;
    }
  }

  static async getReport(gameId) {
    try {
      const response = await $axios().post(`/game/${gameId}/getReport`);
      const result = response.data;
      if (result.success) {
        return result.data;
      }
      return null;
    } catch (err) {
      QLogger.log('Error at GameAPIService.getReport', err);
      ToastService.add({
        type: Constants.ToastTypes.ERROR,
        icon: 'fas fa-times-circle',
        title: i18n('Something went wrong while ending the game'),
      });
    }
  }

  static async fetchQudos(hostId) {
    try {
      const gameServiceBaseUrl = BaseURLS.GAME_SERVICE[getEnvironmentEnv()];
      const response = await $axios().post(`${gameServiceBaseUrl}/getQudos`, {
        hostId,
      });

      return { success: true, data: response.data.data };
    } catch (error) {
      return { success: false, error };
    }
  }

  static async changeHost(payload) {
    try {
      const gameServiceBaseUrl = BaseURLS.GAME_SERVICE[getEnvironmentEnv()];
      const response = await $axios().post(`${gameServiceBaseUrl}/changeHost`, payload);
      return response.data;
    } catch (err) {
      QLogger.log('Error at GameAPIService.changeHost', err);
    }
  }

  static async editQuestion(gameId, questionId, newQuestionBody) {
    const response = await $axios().post(`/game/${gameId}/edit`, {
      modifications: [{
        deletedQuestionId: questionId,
        addedQuestionBody: newQuestionBody,
      }],
    });
    return response.data;
  }

  static async resumeLesson(gameId) {
    try {
      const response = await $axios().post('/game/resumeLesson', { roomHash: gameId });
      return response.data;
    } catch (err) {
      QLogger.log('Error at GameAPIService.resumeLesson', err);
      ToastService.add({
        type: Constants.ToastTypes.ERROR,
        icon: 'fas fa-times-circle',
        title: i18n('Something went wrong while resuming the game'),
      });
    }
  }

  static async undoPlayerGameOver(payload) {
    try {
      const gameServiceBaseUrl = BaseURLS.GAME_SERVICE[getEnvironmentEnv()];
      const response = await $axios().post(`${gameServiceBaseUrl}/v3/undoPlayerGameOver`, payload);
      return response.data;
    } catch (err) {
      QLogger.log('Error at GameAPIService.undoPlayerGameOver', err);
      return {
        success: false,
      };
    }
  }
}
